/* import __COLOCATED_TEMPLATE__ from './publish-button.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import type ArticleContent from 'embercom/models/articles/article-content';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from 'tracked-built-ins';
import { MULTILINGUAL_ARTICLES_FEATURE, ARTICLES_FEATURE } from 'embercom/lib/billing';
import { type ButtonType } from '@intercom/pulse/template-registry';
import { EntityType } from 'embercom/models/data/entity-types';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';
import type ScheduledContentUpdateService from 'embercom/services/scheduled-content-update-service';
import type AppService from 'embercom/services/app-service';
import { type ScheduleMode } from 'embercom/lib/knowledge-hub/constants';

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export interface Args {
  activeContent: ArticleContent;
  publishActiveContent: () => Promise<void>;
  scheduleActiveContentUpdate?: (params: {
    mode: ScheduleMode;
    scheduledDate: string;
    timezone?: string;
  }) => Promise<void>;
  label?: string;
  inRightList?: boolean;
  disabled?: boolean;
  buttonType?: ButtonType | undefined;
}

export default class PublishButton extends Component<Signature> {
  @service declare permissionsService: any;
  @service declare scheduledContentUpdateService: ScheduledContentUpdateService;
  @service declare appService: AppService;

  @tracked showHelpCenterModal = false;
  @tracked showScheduleWillBeDeletedModal = false;
  @tracked showPublishModal = false;
  @tracked isPublishing = false;

  get isUnifiedPublishModalEnabled(): boolean {
    return this.appService.app.canUseUnifiedPublishModal;
  }

  get article() {
    return this.args.activeContent.article;
  }

  get buttonType() {
    return this.args.buttonType || 'primary-live';
  }

  get paywallConfig() {
    let configs = this.args.activeContent.editorConfig.paywallConfig;
    if (configs) {
      if (!this.args.activeContent.isInDefaultLocale) {
        return configs.find((config) => config.featureKey === MULTILINGUAL_ARTICLES_FEATURE);
      }
      return configs.find((config) => config.featureKey === ARTICLES_FEATURE);
    }
    return undefined;
  }

  get hasPendingScheduledContentUpdates() {
    if (
      !this.args.activeContent ||
      this.args.activeContent.entityType !== EntityType.ArticleContent
    ) {
      return false;
    }

    let articleContent = this.args.activeContent as any;
    if (!articleContent.scheduledContentUpdates) {
      return false;
    }

    let pendingUpdates = articleContent.scheduledContentUpdates
      .toArray()
      .filter((update: any) => update.status === ScheduledContentUpdateStatus.PENDING);

    return pendingUpdates.length > 0;
  }

  @action openScheduleWillBeDeletedModal(): void {
    this.showScheduleWillBeDeletedModal = true;
  }

  @action closeScheduleWillBeDeletedModal(): void {
    this.showScheduleWillBeDeletedModal = false;
  }

  @action async publishContent() {
    try {
      await this.permissionsService.checkPermission(
        this.args.activeContent.requiredPublishPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }

    if (this.isUnifiedPublishModalEnabled) {
      this.showPublishModal = true;
      return;
    }

    // Check for pending scheduled updates before publishing
    if (this.hasPendingScheduledContentUpdates) {
      this.openScheduleWillBeDeletedModal();
      return;
    }

    if (this.article?.inCollections.length === 0 && this.args.activeContent.isNotPublished) {
      this.toggleHelpCenterModal(true);
      return;
    }

    await this.publishActiveContent();
  }

  @action closePublishModal(): void {
    this.showPublishModal = false;
  }

  @action async publishActiveContent() {
    this.isPublishing = true;
    await this.args.publishActiveContent();
    this.isPublishing = false;
  }

  @action async proceedWithPublishing() {
    this.closeScheduleWillBeDeletedModal();

    if (this.article?.inCollections.length === 0 && this.args.activeContent.isNotPublished) {
      this.toggleHelpCenterModal(true);
      return;
    }

    await this.publishActiveContent();
  }

  @action toggleHelpCenterModal(state = false): void {
    this.showHelpCenterModal = state;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::PublishButton': typeof PublishButton;
    'knowledge-hub/content-editor/article-content/publish-button': typeof PublishButton;
  }
}
